// 0615l ADR-024 + filosofia CEO: modal viewer-only de Puesto.
//
// Sustituye el drawer lateral previo de PuestosListPage que abria una
// vista parcial con boton "Ver ficha" -> ficha completa. Ahora todo el
// detalle del puesto vive en este modal (centrado, mas legible, mas
// pantalla disponible que el drawer 360px).
//
// SIN acciones de mutacion. Para editar el puesto, asignar persona,
// editar ficha o aprobar -> tab Administracion del modulo M1.

import { useEffect, useState } from 'react';
import { fetchPuesto, fetchCadenaMando, fetchSubordinados } from '../../../shared/services/api';

interface PuestoViewerModalProps {
  puestoId: string | null;
  onClose: () => void;
  onVerFicha?: (puestoId: string) => void;
  onVerOrganigrama?: (puestoId: string) => void;
}

// 0618 fix: el endpoint cadena devuelve {profundidad, puesto: {id, titulo, codigo,
// asignaciones[]}}, no la forma plana {id, titulo, codigo, nivel, persona}.
// Antes el modal renderizaba c.titulo y c.persona que no existen -> barras vacias.
interface CadenaItemRaw {
  profundidad: number;
  puesto: {
    id: string;
    titulo: string;
    codigo?: string | null;
    nivel?: string | null;
    asignaciones?: Array<{
      activo: boolean;
      persona?: { id: string; nombre: string; apellidos: string } | null;
    }>;
  } | null;
}

const COLORS = {
  OSCURO: '#0E243E',
  AZUL1: '#1F3864',
  AZUL2: '#2F5496',
  CELESTE: '#33BCE1',
  GRIS_OSCURO: '#374151',
  GRIS_MEDIO: '#9CA3AF',
  GRIS_SUAVE: '#F3F4F6',
  BORDE: '#E5E7EB',
};

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: COLORS.GRIS_MEDIO,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: COLORS.OSCURO, fontWeight: 500 }}>
        {value && value.trim().length > 0 ? (
          value
        ) : (
          <span style={{ color: COLORS.GRIS_MEDIO }}>—</span>
        )}
      </div>
    </div>
  );
}

export function PuestoViewerModal({
  puestoId,
  onClose,
  onVerFicha,
  onVerOrganigrama,
}: PuestoViewerModalProps) {
  const [puesto, setPuesto] = useState<any>(null);
  const [cadena, setCadena] = useState<CadenaItemRaw[]>([]);
  const [subordinadosCount, setSubordinadosCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!puestoId) {
      setPuesto(null);
      setCadena([]);
      setSubordinadosCount(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchPuesto(puestoId).catch(() => null),
      fetchCadenaMando(puestoId).catch(() => null),
      fetchSubordinados(puestoId).catch(() => null),
    ])
      .then(([pRes, cRes, sRes]) => {
        if (cancelled) return;
        const pData = (pRes as any)?.data ?? null;
        if (!pData) {
          setError('No se pudo cargar el puesto.');
          return;
        }
        setPuesto(pData);
        setCadena(((cRes as any)?.data ?? []) as CadenaItemRaw[]);
        // Soportar 2 formatos: array directo o {hijos / subordinados}.
        const rawSub = (sRes as any)?.data;
        const subArr = Array.isArray(rawSub)
          ? rawSub
          : (rawSub?.hijos ?? rawSub?.subordinados ?? []);
        setSubordinadosCount(Array.isArray(subArr) ? subArr.length : 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error cargando puesto');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [puestoId]);

  // Cierre con ESC
  useEffect(() => {
    if (!puestoId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [puestoId, onClose]);

  if (!puestoId) return null;

  const persona = puesto?.asignaciones?.find((a: any) => a.activo)?.persona ?? puesto?.persona;
  const nivelLabel =
    (puesto?.nivelPuesto?.nombre as string | undefined) ?? puesto?.nivel?.replace(/_/g, ' ');
  const fichaDoc = puesto?.fichaDocumento ?? null;
  const tieneFicha = !!puesto?.fichaDocumentoId;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Detalle de puesto (solo lectura)"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(14,36,62,0.55)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: 'inherit',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: '100%',
          maxWidth: 640,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          padding: 24,
        }}
      >
        {/* Cerrar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: COLORS.GRIS_MEDIO,
              padding: 4,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.GRIS_MEDIO, fontSize: 13 }}>
            Cargando datos del puesto...
          </div>
        )}

        {error && !loading && (
          <div
            style={{
              padding: 16,
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 8,
              color: '#DC2626',
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {puesto && !loading && !error && (
          <>
            {/* Cabecera */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 700,
                    color: COLORS.OSCURO,
                  }}
                >
                  {puesto.titulo}
                </h2>
                {nivelLabel && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: COLORS.GRIS_SUAVE,
                      color: COLORS.GRIS_OSCURO,
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                    }}
                  >
                    {nivelLabel}
                  </span>
                )}
              </div>
              {puesto.codigo && (
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 11,
                    color: COLORS.GRIS_MEDIO,
                    fontWeight: 700,
                  }}
                >
                  {puesto.codigo}
                </div>
              )}
            </div>

            {/* Datos basicos */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 14,
                marginBottom: 18,
                padding: 14,
                background: COLORS.GRIS_SUAVE,
                borderRadius: 8,
              }}
            >
              <Field label="Departamento" value={puesto.departamento?.nombre ?? null} />
              <Field
                label="Organización"
                value={
                  puesto.organizacion?.nombre ?? puesto.departamento?.organizacion?.nombre ?? null
                }
              />
              <Field
                label="Persona asignada"
                value={persona ? `${persona.nombre} ${persona.apellidos ?? ''}`.trim() : null}
              />
              <Field
                label="Subordinados directos"
                value={subordinadosCount > 0 ? String(subordinadosCount) : null}
              />
            </div>

            {/* Cadena de mando — 0618 fix: extraer titulo y persona del shape
                anidado del endpoint. Excluir profundidad 0 (el propio puesto)
                para que solo se vean los superiores. */}
            {(() => {
              const ancestros = cadena.filter((c) => c.profundidad > 0 && c.puesto);
              if (ancestros.length === 0) return null;
              return (
                <>
                  <h3
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: COLORS.AZUL1,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 8,
                    }}
                  >
                    Cadena de mando ({ancestros.length})
                  </h3>
                  <div
                    style={{
                      border: `1px solid ${COLORS.BORDE}`,
                      borderRadius: 6,
                      padding: 8,
                      marginBottom: 16,
                      fontSize: 12,
                      color: COLORS.GRIS_OSCURO,
                    }}
                  >
                    {ancestros.map((c, i) => {
                      const titulo = c.puesto!.titulo;
                      const persona = c.puesto!.asignaciones?.find((a) => a.activo)?.persona;
                      return (
                        <div
                          key={c.puesto!.id}
                          style={{
                            padding: '4px 8px',
                            borderLeft: `2px solid ${COLORS.AZUL2}`,
                            marginLeft: i * 12,
                          }}
                        >
                          <div style={{ fontWeight: 600, color: COLORS.OSCURO }}>{titulo}</div>
                          {persona && (
                            <div style={{ fontSize: 11, color: COLORS.GRIS_MEDIO }}>
                              {persona.nombre} {persona.apellidos}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            {/* Ficha */}
            <h3
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: COLORS.AZUL1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 8,
              }}
            >
              Ficha de puesto
            </h3>
            {tieneFicha ? (
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: COLORS.OSCURO,
                    marginBottom: 8,
                    padding: 10,
                    background: '#F0F4F8',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>
                    {fichaDoc?.titulo ?? 'Ficha de puesto'}
                    {fichaDoc?.estado && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: fichaDoc.estado === 'PUBLICADO' ? '#ECFDF5' : '#FFFBEB',
                          color: fichaDoc.estado === 'PUBLICADO' ? '#059669' : '#D97706',
                        }}
                      >
                        {fichaDoc.estado === 'PUBLICADO' ? 'Vigente' : fichaDoc.estado}
                      </span>
                    )}
                  </span>
                  {onVerFicha && (
                    <button
                      onClick={() => onVerFicha(puesto.id)}
                      style={{
                        padding: '4px 12px',
                        background: COLORS.AZUL2,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Ver ficha
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: COLORS.GRIS_MEDIO,
                  fontStyle: 'italic',
                  marginBottom: 16,
                }}
              >
                Este puesto no tiene ficha asociada.
              </div>
            )}

            {/* Acciones */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                paddingTop: 12,
                borderTop: `1px solid ${COLORS.BORDE}`,
              }}
            >
              {onVerOrganigrama && (
                <button
                  onClick={() => onVerOrganigrama(puesto.id)}
                  style={{
                    padding: '8px 16px',
                    background: '#fff',
                    border: `1px solid ${COLORS.AZUL2}`,
                    color: COLORS.AZUL2,
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Ver en organigrama
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  background: COLORS.GRIS_SUAVE,
                  border: 'none',
                  color: COLORS.GRIS_OSCURO,
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cerrar
              </button>
            </div>

            {/* Footer informativo */}
            <div
              style={{
                marginTop: 12,
                fontSize: 11,
                color: COLORS.GRIS_MEDIO,
                fontStyle: 'italic',
                textAlign: 'right',
              }}
            >
              Vista de solo lectura. Para editar el puesto ve a Estructura → Administración.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
